import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DownloadOutlined } from '@ant-design/icons';
import { Modal, Button, Checkbox, DatePicker, Select, message } from 'antd';
import dayjs from 'dayjs';
import { useProjects } from '@/app/time-tracking/_shared/hooks';
import { exportReport } from '@/services/timeTracking.service';

const { RangePicker } = DatePicker;

const ALL_SHEETS = [
  { key: 'summary', labelKey: 'sheetSummary', adminOnly: false },
  { key: 'by-project', labelKey: 'sheetByProject', adminOnly: false },
  { key: 'by-employee', labelKey: 'sheetByEmployee', adminOnly: true },
  { key: 'by-client', labelKey: 'sheetByClient', adminOnly: true },
  { key: 'daily', labelKey: 'sheetDaily', adminOnly: false },
  { key: 'comparative', labelKey: 'sheetComparative', adminOnly: true },
];

export default function ReportExportModal({
  open,
  onClose,
  company,
  isAdmin,
  defaultDateFrom,
  defaultDateTo,
}) {
  const { t } = useTranslation('forms');

  const availableSheets = useMemo(
    () => ALL_SHEETS.filter((s) => !s.adminOnly || isAdmin),
    [isAdmin]
  );

  const [selectedSheets, setSelectedSheets] = useState(() =>
    availableSheets.map((s) => s.key)
  );
  const [dateRange, setDateRange] = useState(() =>
    defaultDateFrom && defaultDateTo
      ? [dayjs(defaultDateFrom), dayjs(defaultDateTo)]
      : null
  );
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Load projects for dropdown
  const { data: projectsData } = useProjects(company ? { company } : {}, {
    enabled: !!company && open,
  });
  const projectOptions = useMemo(() => {
    const list = projectsData?.data || [];
    return list.map((p) => ({ value: p._id, label: p.name }));
  }, [projectsData]);

  // Task options from selected projects
  const taskOptions = useMemo(() => {
    if (!selectedProjects.length) return [];
    const list = projectsData?.data || [];
    const tasks = [];
    for (const p of list) {
      if (selectedProjects.includes(p._id)) {
        for (const t of p.tasks || []) {
          tasks.push({ value: t._id, label: `${p.name} → ${t.name}` });
        }
      }
    }
    return tasks;
  }, [selectedProjects, projectsData]);

  // Member options from projects data
  const memberOptions = useMemo(() => {
    const list = projectsData?.data || [];
    const map = new Map();
    for (const p of list) {
      for (const m of p.members || []) {
        if (m.employee && !map.has(m.employee._id || m.employee)) {
          const emp = m.employee;
          if (typeof emp === 'object') {
            map.set(emp._id, {
              value: emp._id,
              label: `${emp.firstName || ''} ${emp.lastName || ''}`.trim(),
            });
          }
        }
      }
    }
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [projectsData]);

  const handleSheetToggle = useCallback((key) => {
    setSelectedSheets((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedSheets(availableSheets.map((s) => s.key));
  }, [availableSheets]);

  const handleDeselectAll = useCallback(() => {
    setSelectedSheets([]);
  }, []);

  const handleExport = useCallback(async () => {
    if (!selectedSheets.length) {
      message.warning(t('selectAtLeastOneSheet'));
      return;
    }
    setExporting(true);
    try {
      const params = {
        sheets: selectedSheets.join(','),
        company,
      };
      if (dateRange?.[0]) params.dateFrom = dateRange[0].format('YYYY-MM-DD');
      if (dateRange?.[1]) params.dateTo = dateRange[1].format('YYYY-MM-DD');
      if (selectedEmployees.length)
        params.employees = selectedEmployees.join(',');
      if (selectedProjects.length) params.projects = selectedProjects.join(',');
      if (selectedTask) params.task = selectedTask;

      const blob = await exportReport(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `time-tracking-report.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      onClose();
    } catch {
      message.error(t('exportFailed'));
    } finally {
      setExporting(false);
    }
  }, [
    selectedSheets,
    company,
    dateRange,
    selectedEmployees,
    selectedProjects,
    selectedTask,
    t,
    onClose,
  ]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Modal
      title={t('exportExcel')}
      open={open}
      onCancel={handleClose}
      width={520}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button shape="round" onClick={handleClose}>
            {t('cancel')}
          </Button>
          <Button
            type="primary"
            shape="round"
            className="btn-primary"
            icon={<DownloadOutlined />}
            loading={exporting}
            onClick={handleExport}
            disabled={!selectedSheets.length}
          >
            {t('exportExcel')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {/* Date range */}
        <div>
          <label className="mb-1 block text-sm text-gray-500">
            {t('dateRange')}
          </label>
          <RangePicker
            format="DD-MM-YYYY"
            value={dateRange}
            onChange={setDateRange}
            allowClear
            placeholder={[t('dateFrom'), t('dateTo')]}
            style={{ width: '100%' }}
          />
          <p className="mt-1 text-xs text-gray-400">{t('exportDateHint')}</p>
        </div>

        {/* Sheets selection */}
        <div>
          <div className="mb-1 flex items-center justify-between">
            <label className="text-sm text-gray-500">{t('exportSheets')}</label>
            <div className="flex gap-2 text-xs">
              <button
                type="button"
                className="text-blue-500 hover:underline"
                onClick={handleSelectAll}
              >
                {t('selectAll')}
              </button>
              <span className="text-gray-300">|</span>
              <button
                type="button"
                className="text-blue-500 hover:underline"
                onClick={handleDeselectAll}
              >
                {t('deselectAll')}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {availableSheets.map((s) => (
              <Checkbox
                key={s.key}
                checked={selectedSheets.includes(s.key)}
                onChange={() => handleSheetToggle(s.key)}
              >
                <span className="text-sm">{t(s.labelKey)}</span>
              </Checkbox>
            ))}
          </div>
        </div>

        {/* Filters */}
        {isAdmin && memberOptions.length > 0 && (
          <div>
            <label className="mb-1 block text-sm text-gray-500">
              {t('filterByEmployee')}
            </label>
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={t('allEmployees')}
              options={memberOptions}
              value={selectedEmployees}
              onChange={setSelectedEmployees}
              style={{ width: '100%' }}
              maxTagCount={3}
            />
          </div>
        )}

        {projectOptions.length > 0 && (
          <div>
            <label className="mb-1 block text-sm text-gray-500">
              {t('filterByProject')}
            </label>
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={t('allProjects')}
              options={projectOptions}
              value={selectedProjects}
              onChange={(val) => {
                setSelectedProjects(val);
                setSelectedTask(null);
              }}
              style={{ width: '100%' }}
              maxTagCount={3}
            />
          </div>
        )}

        {taskOptions.length > 0 && (
          <div>
            <label className="mb-1 block text-sm text-gray-500">
              {t('filterByTask')}
            </label>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={t('allTasks')}
              options={taskOptions}
              value={selectedTask}
              onChange={setSelectedTask}
              style={{ width: '100%' }}
            />
          </div>
        )}
      </div>
    </Modal>
  );
}
